import type { ComponentProps } from "react"
import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import { cn } from "@productlane/lib/src/cn"
import { queries } from "@productlane/zero/queries"

import { useThreadContext } from "../../thread-context"
import { LinkedIssues, ThreadIssuesList } from "./linked-issues"
import { LinkedMenuProvider } from "./linked-menu-context"
import { LinkedTags, ThreadTagsList } from "./linked-tags"
import {
	SIDEBAR_GROUP_GUTTER,
	SidebarGroup,
	SidebarGroupLabel,
} from "./shared/sidebar-group"
import {
	ThreadLinkSuggestions,
	ThreadTagSuggestions,
	useThreadLinkSuggestions,
} from "./thread-link-suggestions"
import { ThreadPortalSuggestions } from "./thread-portal-suggestions"
import { ThreadProjectsList } from "./thread-projects-list"

type Props = ComponentProps<"div">

export function LinkedEntities({ className, ...props }: Props) {
	const { thread } = useThreadContext()
	const [projects] = useQuery(queries.projects())
	const [tags] = useQuery(queries.tags())
	const insightProjects = useMemo(() => {
		if (thread && projects && projects.length > 0) {
			return projects.filter((t) =>
				thread.projects.map((p) => p.id).includes(t.id),
			)
		}
		return []
	}, [projects, thread])
	const insightIssues = useMemo(
		() => thread.issues.filter((issue) => !issue.isDeleted),
		[thread.issues],
	)
	const threadTags = useMemo(() => {
		return tags.filter((t) => thread.tags.map((t) => t.id).includes(t.id))
	}, [thread, tags])

	const portalItems = useMemo(
		() => [
			...insightProjects.map((p) => ({
				type: "project" as const,
				id: p.id,
				name: p.name,
				isVisible: p.isVisible,
				portalInstanceIds: p.portalInstanceIds,
			})),
			...insightIssues.map((i) => ({
				type: "issue" as const,
				id: i.id,
				name: i.title,
				isVisible: i.isVisible,
				portalInstanceIds: i.portalInstanceIds,
			})),
		],
		[insightIssues, insightProjects],
	)

	const linkSuggestions = useThreadLinkSuggestions()

	const linkedEntitiesLength = useMemo(() => {
		return [...insightIssues, ...insightProjects, ...threadTags].filter(Boolean)
			.length
	}, [insightIssues, insightProjects, threadTags])

	return (
		<>
			<SidebarGroup className={className} {...props}>
				<SidebarGroupLabel>Links</SidebarGroupLabel>
				<div>
					<LinkedMenuProvider>
						<div
							className={cn(SIDEBAR_GROUP_GUTTER, "-mx-1 flex flex-col", {
								hidden:
									!linkedEntitiesLength &&
									linkSuggestions.tagSuggestions.length === 0,
							})}
						>
							<ThreadProjectsList />
							<ThreadIssuesList />
							<ThreadPortalSuggestions items={portalItems} />
							<div className="mt-1.5 flex flex-row flex-wrap items-center gap-1.5">
								<ThreadTagsList
									suggestions={
										<ThreadTagSuggestions
											key={thread.id}
											state={linkSuggestions}
										/>
									}
								/>
							</div>
						</div>
						<div className="-mx-1 mt-2 flex flex-row gap-2 px-2 [&_a:hover]:!bg-gray-150 [&_a:hover]:!text-primary dark:[&_a:hover]:!bg-gray-800 [&_a:hover_svg]:!text-secondary [&_a]:!text-secondary [&_a_svg]:!text-tertiary [&_button:hover]:!bg-gray-150 [&_button:hover]:!text-primary dark:[&_button:hover]:!bg-gray-800 [&_button:hover_svg]:!text-secondary [&_button]:!text-secondary [&_button_svg]:!text-tertiary">
							<LinkedIssues />
							<LinkedTags />
						</div>
					</LinkedMenuProvider>
				</div>
			</SidebarGroup>
			{/* Its own section, so its spacing matches Links and Customer. Stays mounted
		    while hidden so cards that arrive after the search still animate in. */}
			<SidebarGroup
				className={cn({
					hidden:
						!linkSuggestions.searching &&
						linkSuggestions.suggestions.length === 0,
				})}
			>
				<SidebarGroupLabel>Suggested</SidebarGroupLabel>
				<div className={cn(SIDEBAR_GROUP_GUTTER, "-mx-1 flex flex-col")}>
					<ThreadLinkSuggestions key={thread.id} state={linkSuggestions} />
				</div>
			</SidebarGroup>
		</>
	)
}
